import { ChevronDownIcon } from "lucide-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import {
  type ContextWindowSnapshot,
  deriveCachedInputRate,
  formatContextWindowPercentage,
  formatContextWindowTokens,
} from "~/lib/contextWindow";
import {
  isProviderUsageNearLimit,
  type ProviderAccountUsagePresentation,
} from "~/lib/providerUsage";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

function isDomNode(value: unknown): value is Node {
  return typeof value === "object" && value !== null && "nodeType" in value;
}

const contextWindowActionButtonClassName =
  "h-5 shrink-0 cursor-pointer rounded-sm border border-border/70 px-1.5 font-medium text-[10px] text-foreground leading-none transition-colors hover:border-border hover:bg-muted hover:text-foreground focus-visible:border-focus-ring focus-visible:bg-muted focus-visible:outline-none disabled:pointer-events-none disabled:cursor-default disabled:opacity-55";

function AccountUsageBar(props: {
  usageLabel: string;
  rowLabel: string;
  detail: string;
  usedPercent: number;
  warning: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex min-w-0 items-baseline justify-between gap-3 text-xs">
        <span className="shrink-0 font-medium text-foreground">{props.rowLabel}</span>
        <span className="min-w-0 text-right text-muted-foreground">{props.detail}</span>
      </div>
      <div
        role="meter"
        aria-label={`${props.usageLabel} ${props.rowLabel} ${props.usedPercent}% used`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={props.usedPercent}
        className="h-1 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width]",
            props.warning ? "bg-warning" : "bg-primary",
          )}
          style={{ width: `${props.usedPercent}%` }}
        />
      </div>
    </div>
  );
}

/** Categorical swatches assigned by index (cycled), so a category keeps its
 *  color as long as the provider keeps its order. */
const CONTEXT_CATEGORY_COLOR_CLASS_NAMES = [
  "bg-context-cat-1",
  "bg-context-cat-2",
  "bg-context-cat-3",
  "bg-context-cat-4",
  "bg-context-cat-5",
  "bg-context-cat-6",
  "bg-context-cat-7",
  "bg-context-cat-8",
] as const;

type ContextBreakdownSegment = {
  readonly key: string;
  readonly name: string;
  readonly tokens: number;
  readonly percentage: number;
  readonly colorClassName: string;
};

/** Providers without a category breakdown (Codex) collapse to one used-vs-free
 *  segment so the bar renders through the same path. */
function buildContextBreakdownSegments(
  usage: ContextWindowSnapshot | null,
): ReadonlyArray<ContextBreakdownSegment> {
  const maxTokens = usage?.maxTokens ?? null;
  if (!usage || maxTokens === null || maxTokens <= 0) {
    return [];
  }

  const toPercentage = (tokens: number) => Math.max(0, Math.min(100, (tokens / maxTokens) * 100));
  const categories = usage.contextCategories ?? null;
  if (categories && categories.length > 0) {
    // Colors are keyed to the provider's order (stable across updates), then
    // the bar and legend display largest-first.
    return categories
      .map((category, index) => ({
        key: `${index}-${category.name}`,
        name: category.name,
        tokens: category.tokens,
        percentage: toPercentage(category.tokens),
        colorClassName:
          CONTEXT_CATEGORY_COLOR_CLASS_NAMES[index % CONTEXT_CATEGORY_COLOR_CLASS_NAMES.length] ??
          CONTEXT_CATEGORY_COLOR_CLASS_NAMES[0],
      }))
      .sort((left, right) => right.tokens - left.tokens);
  }

  if (usage.usedTokens <= 0) {
    return [];
  }
  return [
    {
      key: "used",
      name: "Used",
      tokens: usage.usedTokens,
      percentage: usage.usedPercentage ?? toPercentage(usage.usedTokens),
      colorClassName: "bg-primary",
    },
  ];
}

function ContextBreakdownLegendRow(props: {
  swatchClassName: string;
  name: string;
  tokens: number | null;
  percentage: number | null;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 leading-tight">
      <span
        aria-hidden="true"
        className={cn("size-2 shrink-0 rounded-sm", props.swatchClassName)}
      />
      <span className="min-w-0 flex-1 truncate text-foreground text-xs">{props.name}</span>
      <span className="shrink-0 text-muted-foreground text-xs">
        {formatContextWindowTokens(props.tokens)}
      </span>
      <span className="w-9 shrink-0 text-right text-foreground text-xs tabular-nums">
        {formatContextWindowPercentage(props.percentage) ?? "–"}
      </span>
    </div>
  );
}

export function ContextWindowMeter(props: {
  usage: ContextWindowSnapshot | null;
  accountUsage?: ProviderAccountUsagePresentation | null;
  contextWindowLabel?: string | null;
  onResetAccountUsage?: (() => void) | undefined;
  accountUsageResetInFlight?: boolean | undefined;
  onCompactContext?: (() => void) | undefined;
  contextCompactDisabled?: boolean | undefined;
  contextCompactInFlight?: boolean | undefined;
  contextCompactDisabledReason?: string | null | undefined;
}) {
  const { usage, contextWindowLabel } = props;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const suppressHoverOpenRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const [isHoverOpenSuppressed, setIsHoverOpenSuppressed] = useState(false);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const accountUsage = props.accountUsage ?? null;
  const breakdownSegments = buildContextBreakdownSegments(usage);
  const hasContextCategories = (usage?.contextCategories?.length ?? 0) > 0;
  const cachedInputRate = deriveCachedInputRate(usage);
  const cachedInputDetail = cachedInputRate
    ? `${formatContextWindowPercentage(cachedInputRate.percentage) ?? "0%"} ⋅ ${formatContextWindowTokens(
        cachedInputRate.cachedTokens,
      )} of ${formatContextWindowTokens(cachedInputRate.inputTokens)} input tokens`
    : null;
  const usageNearLimit = isProviderUsageNearLimit(accountUsage);
  const usedPercentage = formatContextWindowPercentage(usage?.usedPercentage ?? null);
  const normalizedPercentage = Math.max(0, Math.min(100, usage?.usedPercentage ?? 0));
  const radius = 9.75;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (normalizedPercentage / 100) * circumference;
  const showCompactButton =
    props.onCompactContext !== undefined || props.contextCompactDisabledReason !== undefined;
  const compactButtonDisabled =
    props.contextCompactDisabled === true ||
    props.contextCompactInFlight === true ||
    props.onCompactContext === undefined;
  const compactButtonTooltip =
    props.contextCompactDisabledReason ??
    (props.contextCompactInFlight === true
      ? "Context compaction is running."
      : "Best used near the context limit.");

  const contextAriaLabel = !usage
    ? "Context window — no tokens used yet"
    : usage.maxTokens !== null && usedPercentage
      ? `Context window ${usedPercentage} used`
      : `Context window ${formatContextWindowTokens(usage.usedTokens)} tokens used`;
  const onOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen && suppressHoverOpenRef.current) {
      return;
    }
    setIsOpen(nextOpen);
    if (!nextOpen) {
      setIsPinnedOpen(false);
    }
  }, []);
  const allowHoverOpen = useCallback(() => {
    suppressHoverOpenRef.current = false;
    setIsHoverOpenSuppressed(false);
  }, []);
  const onTogglePinnedOpen = useCallback(() => {
    allowHoverOpen();
    setIsPinnedOpen((currentPinnedOpen) => {
      const nextPinnedOpen = !currentPinnedOpen;
      setIsOpen(nextPinnedOpen);
      return nextPinnedOpen;
    });
  }, [allowHoverOpen]);
  useLayoutEffect(() => {
    if (!isOpen) return;

    const handleDocumentInteraction = (event: Event) => {
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      const targetNodes = path.filter(isDomNode);
      if (targetNodes.length === 0 && isDomNode(event.target)) {
        targetNodes.push(event.target);
      }
      if (
        targetNodes.some(
          (target) => triggerRef.current?.contains(target) || popupRef.current?.contains(target),
        )
      ) {
        return;
      }
      suppressHoverOpenRef.current = true;
      setIsHoverOpenSuppressed(true);
      onOpenChange(false);
    };

    document.addEventListener("pointerdown", handleDocumentInteraction, true);
    document.addEventListener("mousedown", handleDocumentInteraction, true);
    document.addEventListener("click", handleDocumentInteraction, true);
    document.addEventListener("focusin", handleDocumentInteraction, true);
    return () => {
      document.removeEventListener("pointerdown", handleDocumentInteraction, true);
      document.removeEventListener("mousedown", handleDocumentInteraction, true);
      document.removeEventListener("click", handleDocumentInteraction, true);
      document.removeEventListener("focusin", handleDocumentInteraction, true);
    };
  }, [isOpen, onOpenChange]);
  const isMeterActive = isOpen || isPinnedOpen;

  return (
    <Popover onOpenChange={onOpenChange} open={isOpen}>
      <PopoverTrigger
        openOnHover={!isPinnedOpen && !isHoverOpenSuppressed}
        delay={150}
        closeDelay={0}
        render={
          <button
            ref={triggerRef}
            type="button"
            className={cn(
              "group/context-meter inline-flex size-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground outline-none ring-1 ring-transparent transition-[background-color,box-shadow,color] duration-200",
              "hover:bg-muted/45 hover:text-foreground hover:ring-border/70 focus-visible:bg-muted/45 focus-visible:text-foreground focus-ring",
              isMeterActive && "bg-muted/45 text-foreground ring-border/70",
            )}
            aria-expanded={isOpen}
            aria-label={
              usageNearLimit && accountUsage
                ? `${contextAriaLabel}, ${accountUsage.label} near limit`
                : contextAriaLabel
            }
            onClickCapture={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onTogglePinnedOpen();
            }}
            onPointerLeave={allowHoverOpen}
          >
            <span className="relative flex h-6 w-6 items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                className="-rotate-90 absolute inset-0 h-full w-full transform-gpu"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r={radius}
                  fill="none"
                  stroke="color-mix(in oklab, var(--color-muted) 70%, transparent)"
                  strokeWidth="3"
                  className="transition-[stroke] duration-200 group-hover/context-meter:stroke-muted-foreground/35 group-focus-visible/context-meter:stroke-muted-foreground/35 motion-reduce:transition-none"
                />
                {usage ? (
                  <circle
                    cx="12"
                    cy="12"
                    r={radius}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    className="transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none"
                  />
                ) : null}
              </svg>
              {usage ? (
                <span
                  className={cn(
                    "relative flex h-[15px] w-[15px] items-center justify-center rounded-full bg-background text-[8px] font-medium",
                    "text-muted-foreground",
                  )}
                >
                  {usage.usedPercentage !== null
                    ? Math.trunc(usage.usedPercentage)
                    : formatContextWindowTokens(usage.usedTokens)}
                </span>
              ) : null}
              {usageNearLimit ? (
                <span
                  className="absolute -right-px -top-px h-1.5 w-1.5 rounded-full bg-warning ring-2 ring-background"
                  aria-hidden="true"
                />
              ) : null}
            </span>
          </button>
        }
      />
      <PopoverPopup
        ref={popupRef}
        tooltipStyle
        side="top"
        align="end"
        className="w-96 max-w-[calc(100vw-2rem)] px-3 py-2"
      >
        <div className="space-y-2 leading-tight">
          <div className="space-y-1.5">
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Context window
            </div>
            <div className="flex min-w-0 items-center justify-between gap-2">
              {!usage ? (
                <div className="whitespace-nowrap text-xs font-medium text-foreground">
                  <span>No tokens used yet</span>
                  {contextWindowLabel ? (
                    <>
                      <span className="mx-1">⋅</span>
                      <span>{contextWindowLabel} window</span>
                    </>
                  ) : null}
                </div>
              ) : usage.maxTokens !== null && usedPercentage ? (
                <div className="whitespace-nowrap text-xs font-medium text-foreground">
                  <span>{usedPercentage}</span>
                  <span className="mx-1">⋅</span>
                  <span>{formatContextWindowTokens(usage.usedTokens)}</span>
                  <span>/</span>
                  <span>{formatContextWindowTokens(usage.maxTokens ?? null)} context used</span>
                </div>
              ) : (
                <div className="text-sm text-foreground">
                  {formatContextWindowTokens(usage.usedTokens)} tokens used so far
                </div>
              )}
              {hasContextCategories ? (
                <button
                  type="button"
                  aria-expanded={isBreakdownOpen}
                  aria-label="Show context breakdown"
                  onClick={() => setIsBreakdownOpen((open) => !open)}
                  className="-mr-0.5 inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:outline-none"
                >
                  <ChevronDownIcon
                    aria-hidden="true"
                    className={cn("size-3.5 transition-transform", isBreakdownOpen && "rotate-180")}
                  />
                </button>
              ) : null}
            </div>
            {breakdownSegments.length > 0 ? (
              <div aria-hidden="true" className="flex h-1 overflow-hidden rounded-full bg-muted">
                {breakdownSegments.map((segment) => (
                  <div
                    key={segment.key}
                    data-context-segment={segment.name}
                    className={cn("h-full shrink-0 transition-[width]", segment.colorClassName)}
                    style={{ width: `${segment.percentage}%` }}
                  />
                ))}
              </div>
            ) : null}
            {hasContextCategories && isBreakdownOpen ? (
              <div className="space-y-1 pt-0.5">
                {breakdownSegments.map((segment) => (
                  <ContextBreakdownLegendRow
                    key={segment.key}
                    swatchClassName={segment.colorClassName}
                    name={segment.name}
                    tokens={segment.tokens}
                    percentage={segment.percentage}
                  />
                ))}
                <ContextBreakdownLegendRow
                  swatchClassName="bg-muted"
                  name="Free space"
                  tokens={usage?.remainingTokens ?? null}
                  percentage={usage?.remainingPercentage ?? null}
                />
              </div>
            ) : null}
            {cachedInputDetail ? (
              <div className="flex min-w-0 items-baseline gap-2 text-xs">
                <span className="shrink-0 font-medium text-foreground">Cached input</span>
                <span className="min-w-0 text-muted-foreground">{cachedInputDetail}</span>
              </div>
            ) : null}
            {usage &&
            (usage.totalProcessedTokens ?? null) !== null &&
            (usage.totalProcessedTokens ?? 0) > usage.usedTokens ? (
              <div className="text-xs text-muted-foreground">
                Total processed: {formatContextWindowTokens(usage.totalProcessedTokens ?? null)}{" "}
                tokens
              </div>
            ) : null}
            {usage?.compactsAutomatically || showCompactButton ? (
              <div className="flex min-w-0 items-center justify-between gap-3 text-xs text-muted-foreground">
                <span className="min-w-0">
                  {usage?.compactsAutomatically
                    ? "Automatically compacts its context when needed."
                    : "Manual context compaction is available."}
                </span>
                {showCompactButton ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span
                          className={cn(
                            "inline-flex shrink-0",
                            compactButtonDisabled ? "cursor-default" : "cursor-pointer",
                          )}
                        >
                          <button
                            type="button"
                            disabled={compactButtonDisabled}
                            onClick={props.onCompactContext}
                            className={contextWindowActionButtonClassName}
                          >
                            {props.contextCompactInFlight ? "Compacting" : "Compact now"}
                          </button>
                        </span>
                      }
                    />
                    <TooltipPopup side="top" align="end" className="max-w-64">
                      {compactButtonTooltip}
                    </TooltipPopup>
                  </Tooltip>
                ) : null}
              </div>
            ) : null}
          </div>
          {accountUsage ? (
            <div className="space-y-1.5 border-border/60 border-t pt-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                {accountUsage.label}
              </div>
              {accountUsage.resetCredits ? (
                <div className="flex min-w-0 items-center gap-2 text-xs">
                  <span className="shrink-0 font-medium text-foreground">Resets</span>
                  <span className="min-w-0 truncate text-muted-foreground">
                    {accountUsage.resetCredits.shortLabel}
                    <span className="mx-1" aria-hidden>
                      -
                    </span>
                    {accountUsage.resetCredits.detail}
                  </span>
                  {props.onResetAccountUsage && accountUsage.resetCredits.availableCount > 0 ? (
                    <button
                      type="button"
                      disabled={props.accountUsageResetInFlight === true}
                      onClick={props.onResetAccountUsage}
                      className={cn("relative", contextWindowActionButtonClassName)}
                      aria-label={
                        accountUsage.resetCredits.expirationUrgency
                          ? "Reset usage (a reset expires soon)"
                          : undefined
                      }
                    >
                      {props.accountUsageResetInFlight ? "Resetting" : "Reset"}
                      {accountUsage.resetCredits.expirationUrgency ? (
                        <span
                          aria-hidden="true"
                          className={cn(
                            "-right-px -top-px absolute h-1.5 w-1.5 rounded-full ring-2 ring-popover",
                            accountUsage.resetCredits.expirationUrgency === "critical"
                              ? "bg-destructive"
                              : "bg-warning",
                          )}
                        />
                      ) : null}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {accountUsage.spendControl ? (
                <AccountUsageBar
                  usageLabel={accountUsage.label}
                  rowLabel={accountUsage.spendControl.label}
                  detail={accountUsage.spendControl.detail}
                  usedPercent={accountUsage.spendControl.usedPercent}
                  warning={accountUsage.spendControl.warning}
                />
              ) : null}
              {accountUsage.windows.map((window) => (
                <AccountUsageBar
                  key={window.key}
                  usageLabel={accountUsage.label}
                  rowLabel={window.label}
                  detail={window.detail}
                  usedPercent={window.usedPercent}
                  warning={window.warning}
                />
              ))}
            </div>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
